import { Link, useFetcher } from "@remix-run/react";
import { Map, Pencil, Share2 } from "lucide-react";
import * as React from "react";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { Button } from "#app/components/ui/button.tsx";
import { Card } from "#app/components/ui/card.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "#app/components/ui/dialog.tsx";
import { Icon } from "#app/components/ui/icon.tsx";
import { Label } from "#app/components/ui/label.tsx";
import { RadioGroup, RadioGroupItem } from "#app/components/ui/radio-group.tsx";
import { Textarea } from "#app/components/ui/textarea.tsx";
import { FavoriteButton } from "./favorite-button";

// Define props type
interface ActionButtonsProps {
  station: {
    id: number;
    name: string;
    url: string;
  } | null;
  user: any; // Replace with specific user type
  favorites: number[] | undefined;
  favoriteFetcher: any; // Type appropriately
  emailFetcher: any; // Type appropriately
}

export function ActionButtons({
  station,
  user,
  favorites,
  favoriteFetcher,
  emailFetcher,
}: ActionButtonsProps) {
  if (!station) {
    return null;
  }

  const isFavorite = favorites?.includes(station.id);

  const handleShare = () => {
    if (navigator.share && station) {
      navigator
        .share({
          title: `Estação de Carregamento: ${station.name}`,
          text: `Confira esta estação de carregamento no Carregados: ${station.name}`,
          url: window.location.href,
        })
        .catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href).then(
        () => {
          alert("Link copiado para a área de transferência!");
        },
        (err) => {
          console.error("Erro ao copiar: ", err);
        },
      );
    }
  };

  return (
    <Card className='overflow-hidden bg-white shadow-sm backdrop-blur-sm'>
      <div className='p-4'>
        <FavoriteButton
          stationId={station.id}
          stationUrl={station.url}
          userId={user?.id}
          isFavorite={!!isFavorite}
          fetcher={favoriteFetcher}
        />

        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <Button
            variant="secondary"
            className="flex items-center justify-center rounded-lg border-none bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm transition-all hover:shadow-md dark:from-blue-900/30 dark:to-blue-900/20 dark:text-gray-200 dark:hover:text-white"
            onClick={handleShare}
          >
            <Share2 className="mr-2 h-5 w-5" />
            Compartilhar
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="secondary"
                className="flex items-center justify-center rounded-lg border-none bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm transition-all hover:shadow-md dark:from-blue-900/30 dark:to-blue-900/20 dark:text-gray-200 dark:hover:text-white"
              >
                <Pencil className="mr-2 h-5 w-5" />
                Corrigir Informações
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[500px] overflow-auto dark:bg-gray-800 dark:border-gray-700">
              <DialogHeader>
                <DialogTitle className="dark:text-white">
                  Corrigir Informações
                </DialogTitle>
              </DialogHeader>
              <div className="flex min-h-full flex-col justify-center">
                <div className="mx-auto w-full max-w-md">
                  {emailFetcher?.data?.message !== "success" ? (
                    <div className="dark:text-gray-300">
                      <div className="mx-auto w-full max-w-md px-8">
                        <emailFetcher.Form
                          encType="multipart/form-data"
                          method="post"
                        >
                          <HoneypotInputs />
                          <div className="flex flex-col space-y-6">
                            <div className="grid w-full gap-1.5">
                              <Label htmlFor="active">
                                A estação está ativa?
                              </Label>
                              <RadioGroup
                                name="active"
                                id="active"
                                defaultValue="sim"
                                className="dark:text-gray-300"
                              >
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value="sim"
                                    id="sim"
                                    className="dark:border-gray-600 dark:data-[state=checked]:bg-blue-500"
                                  />
                                  <Label htmlFor="sim">Sim</Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value="nao"
                                    id="nao"
                                    className="dark:border-gray-600 dark:data-[state=checked]:bg-blue-500"
                                  />
                                  <Label htmlFor="nao">Não</Label>
                                </div>
                              </RadioGroup>
                            </div>

                            <div className="grid w-full gap-1.5">
                              <Label htmlFor="content">
                                Quais informações ou correções você quer
                                adicionar?
                              </Label>
                              <Textarea
                                name="content"
                                placeholder="Escreva aqui."
                                id="content"
                                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              />
                            </div>
                            <input
                              type="hidden"
                              name="locationId"
                              value={station.id}
                            />
                            <input type="hidden" name="userId" value={user?.id} />
                            <input type="hidden" name="intent" value="info" />
                            <div className="flex items-center justify-between gap-6 pt-3">
                              <Button
                                type="submit"
                                className="w-full dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white"
                                disabled={emailFetcher.state === "submitting"}
                              >
                                {emailFetcher.state === "submitting"
                                  ? "Enviando..."
                                  : "Enviar"}
                              </Button>
                            </div>
                          </div>
                        </emailFetcher.Form>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center space-y-4">
                      <Icon name="check" className="text-7xl text-green-600" />
                      <p className="text-center text-xl font-semibold dark:text-white">
                        Enviado com sucesso! As informações serão verificadas e
                        alteradas ou corrigidas. Obrigado!
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-4">
          <h3 className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400">
            Navegação
          </h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Button
              variant="secondary"
              className="flex items-center justify-center rounded-lg border-none bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm transition-all hover:shadow-md dark:from-blue-900/30 dark:to-blue-900/20 dark:text-gray-200 dark:hover:text-white"
              asChild
            >
              <a
                href={`https://carregados.com.br/redirect/location?locationid=${station.id}&target=maps`}
                target="_blank"
                rel="nofollow noopener"
              >
                <Map className="mr-2 h-5 w-5" />
                Google Maps
              </a>
            </Button>
            <Button
              variant="secondary"
              className="flex items-center justify-center rounded-lg border-none bg-gradient-to-r from-blue-50 to-blue-100 shadow-sm transition-all hover:shadow-md dark:from-blue-900/30 dark:to-blue-900/20 dark:text-gray-200 dark:hover:text-white"
              asChild
            >
              <a
                href={`https://carregados.com.br/redirect/location?locationid=${station.id}&target=waze`}
                target="_blank"
                rel="nofollow noopener"
              >
                <Map className="mr-2 h-5 w-5" />
                Waze
              </a>
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
